import { Pencil } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { renameJob } from "../api";

/**
 * The clip's display title with an inline rename.
 *
 * `title` is the server's display title (never the id, never an upload
 * filename — see jobStore `displayTitle`). The pencil turns the text into a
 * prefilled, select-all input: Enter saves, Esc cancels, blur saves when the
 * text changed. The save is optimistic — the new title shows at once and
 * reverts with a one-line error under it if the request fails. The stored
 * title (trimmed by the server) reaches the parent through `onRenamed`.
 *
 * One edit session settles exactly once: React still dispatches `focusout`
 * on the input it removes after Enter/Esc, so without `settledRef` the blur
 * ran a second commit (two PATCHes, two `onRenamed`).
 *
 * `data-no-enter` on the input keeps the Brand Setup wizard's StepFrame from
 * treating Enter as "Continue" should this ever be mounted inside one.
 * `dir="auto"` lets an Arabic title (or the bidi-isolated caption excerpt in
 * a derived one) lay out right-to-left on its own.
 */
export function JobTitle({
  jobId,
  title,
  onRenamed,
  style,
}: {
  jobId: string;
  title: string;
  onRenamed?: (title: string) => void;
  style?: CSSProperties;
}): React.JSX.Element {
  const [shown, setShown] = useState(title);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pencilRef = useRef<HTMLButtonElement | null>(null);
  // True once the current edit session has committed or cancelled; every
  // later commit() for that session (the unmount blur) is a no-op.
  const settledRef = useRef(false);
  // How the session ended: a keyboard exit (Enter/Esc) returns focus to the
  // pencil once it is mounted again; a mouse blur leaves focus where the
  // user put it.
  const exitRef = useRef<"keyboard" | "blur" | null>(null);

  // The parent re-fetched (a re-render, a rename elsewhere): follow it unless
  // the user is mid-edit.
  useEffect(() => {
    setShown(title);
    if (!editing) setDraft(title);
  }, [title]); // deliberately not `editing`: a keystroke must not reset the draft

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
      return;
    }
    // The pencil is unmounted while the input is up, so the refocus has to
    // wait for the render that brings it back — hence an effect, not the
    // handler.
    if (exitRef.current === "keyboard") {
      exitRef.current = null;
      pencilRef.current?.focus();
    }
  }, [editing]);

  const begin = (): void => {
    settledRef.current = false;
    exitRef.current = null;
    setDraft(shown);
    setError(null);
    setEditing(true);
  };

  const cancel = (): void => {
    if (settledRef.current) return;
    settledRef.current = true;
    exitRef.current = "keyboard";
    setDraft(shown);
    setEditing(false);
  };

  const commit = async (): Promise<void> => {
    if (settledRef.current) return;
    settledRef.current = true;
    const next = draft.trim();
    setEditing(false);
    if (!next || next === shown) {
      setDraft(shown);
      return;
    }
    const previous = shown;
    setShown(next);
    setSaving(true);
    setError(null);
    try {
      const r = await renameJob(jobId, next);
      setShown(r.title);
      setDraft(r.title);
      onRenamed?.(r.title);
    } catch (err) {
      setShown(previous);
      setDraft(previous);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      exitRef.current = "keyboard";
      void commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  };

  const onBlur = (): void => {
    // A blur that follows Enter/Esc is the unmount, not a decision — keep
    // the keyboard exit reason so the pencil gets focus back.
    if (!settledRef.current) exitRef.current = "blur";
    void commit();
  };

  const textStyle: CSSProperties = {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    lineHeight: 1.3,
    fontFamily: "inherit",
    color: "var(--ql-fg)",
  };

  return (
    <div style={{ minWidth: 0, ...style }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, minHeight: 26 }}>
        {editing ? (
          <input
            ref={inputRef}
            data-no-enter
            dir="auto"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            maxLength={120}
            aria-label="Clip title"
            style={{
              ...textStyle,
              flex: 1,
              minWidth: 0,
              width: "100%",
              boxSizing: "border-box",
              padding: "2px 8px",
              margin: "-3px -9px",
              borderRadius: 8,
              background: "rgba(255,255,255,.05)",
              border: "1px solid rgba(255,213,0,.45)",
              outline: "none",
            }}
          />
        ) : (
          <>
            <div
              dir="auto"
              title={shown}
              style={{
                ...textStyle,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {shown}
            </div>
            <button
              ref={pencilRef}
              type="button"
              onClick={begin}
              disabled={saving}
              aria-label="Rename"
              title="Rename"
              style={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 26,
                height: 26,
                padding: 0,
                borderRadius: 7,
                border: "1px solid transparent",
                background: "transparent",
                color: "var(--ql-fg-subtle)",
                cursor: saving ? "default" : "pointer",
              }}
            >
              <Pencil size={14} strokeWidth={2} />
            </button>
          </>
        )}
      </div>
      {error && (
        <div role="alert" style={{ marginTop: 4, fontSize: 12, color: "var(--ql-danger)" }}>
          Couldn't rename: {error}
        </div>
      )}
    </div>
  );
}
